import * as THREE from 'three';
import type { Quality } from '../core/settings';
import { seededRandom } from '../game/rng';
import { fbm, smoothstep, valueNoise } from '../utils/math';
import { GeometryBuilder, Templates as T, trs } from './GeometryBuilder';
import { LAYOUT, PATHS, YARD_TREES, isOpenGround } from './layout';
import { addWind, toon, toonUnique } from './materials';

const FLAT_RADIUS = 50;
const WORLD_RADIUS = 1400;

/** Ground height: perfectly flat around the homestead, rolling hills beyond. */
export function groundHeight(x: number, z: number): number {
  const r = Math.hypot(x, z);
  const hills = smoothstep(FLAT_RADIUS, 170, r) * (fbm(x * 0.008, z * 0.008, 4) * 16 + 4);
  const foothills = smoothstep(260, 460, r) * 45;
  return hills + foothills;
}

const GRASS_DARK = new THREE.Color('#7cc45c');
const GRASS_LIGHT = new THREE.Color('#a8e07a');
const GRASS_GOLD = new THREE.Color('#cfd97a');
const GRASS_FAR = new THREE.Color('#9cc98a');

function buildGround(): THREE.Mesh {
  // A polar grid with rings packed densely near the farm and sparsely far away.
  const rings = 70;
  const segments = 120;
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const color = new THREE.Color();

  positions.push(0, 0, 0);
  color.copy(GRASS_LIGHT);
  colors.push(color.r, color.g, color.b);
  for (let ring = 1; ring <= rings; ring++) {
    const radius = WORLD_RADIUS * Math.pow(ring / rings, 2.2);
    for (let s = 0; s < segments; s++) {
      const angle = (s / segments) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      positions.push(x, groundHeight(x, z), z);
      const n = fbm(x * 0.03, z * 0.03, 3);
      color.copy(GRASS_DARK).lerp(GRASS_LIGHT, THREE.MathUtils.clamp(0.55 + n, 0, 1));
      if (valueNoise(x * 0.012 + 50, z * 0.012) > 0.35) color.lerp(GRASS_GOLD, 0.45);
      color.lerp(GRASS_FAR, smoothstep(150, 600, radius) * 0.6);
      colors.push(color.r, color.g, color.b);
    }
  }
  for (let s = 0; s < segments; s++) indices.push(0, 1 + ((s + 1) % segments), 1 + s);
  for (let ring = 1; ring < rings; ring++) {
    const a0 = 1 + (ring - 1) * segments;
    const b0 = 1 + ring * segments;
    for (let s = 0; s < segments; s++) {
      const s1 = (s + 1) % segments;
      indices.push(a0 + s, a0 + s1, b0 + s);
      indices.push(a0 + s1, b0 + s1, b0 + s);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.receiveShadow = true;
  mesh.name = 'ground';
  return mesh;
}

function buildMountains(): THREE.Mesh {
  const b = new GeometryBuilder();
  const rand = seededRandom(9001);
  const ring = (
    count: number,
    minR: number,
    maxR: number,
    base: [number, number],
    height: [number, number],
    tint: string,
  ) => {
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + rand() * 0.15;
      const r = minR + rand() * (maxR - minR);
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const radius = base[0] + rand() * (base[1] - base[0]);
      const h = height[0] + rand() * (height[1] - height[0]);
      const segments = 6 + Math.floor(rand() * 3);
      const cone = new THREE.ConeGeometry(1, 1, segments, 4).translate(0, 0.5, 0);
      // Deterministic jitter keyed on position so seam vertices stay welded.
      const pos = cone.getAttribute('position');
      for (let v = 0; v < pos.count; v++) {
        const px = pos.getX(v);
        const py = pos.getY(v);
        const pz = pos.getZ(v);
        if (py > 0.99 || py < 0.01) continue;
        const n = valueNoise(px * 3.1 + i, pz * 3.1 + py * 5) * 0.18;
        pos.setXYZ(v, px * (1 + n), py + n * 0.15, pz * (1 + n));
      }
      // Colour bands by height: foothill green → slate blue → snow cap.
      const c = new THREE.Color();
      const snowLine = 0.62 + rand() * 0.12;
      const geometry = cone.toNonIndexed();
      const flatPos = geometry.getAttribute('position');
      const flatColors = new Float32Array(flatPos.count * 3);
      for (let v = 0; v < flatPos.count; v++) {
        const y = flatPos.getY(v);
        if (y > snowLine) c.set('#ffffff');
        else if (y > snowLine - 0.1) c.set('#dfe5f5');
        else if (y < 0.12) c.set('#7fa77a');
        else c.set(tint).lerp(new THREE.Color('#a9b5d9'), y);
        flatColors[v * 3] = c.r;
        flatColors[v * 3 + 1] = c.g;
        flatColors[v * 3 + 2] = c.b;
      }
      geometry.deleteAttribute('uv');
      geometry.setAttribute('color', new THREE.BufferAttribute(flatColors, 3));
      geometry.applyMatrix4(
        trs([x, groundHeight(x, z) - 8, z], [0, rand() * 6.28, 0], [radius, h, radius]),
      );
      geometry.computeVertexNormals();
      b.addPrepared(geometry);
      cone.dispose();
    }
  };
  ring(34, 520, 640, [70, 130], [80, 150], '#7f93c4');
  ring(26, 760, 900, [130, 200], [150, 240], '#93a0cf');

  const { base } = b.build();
  const mesh = new THREE.Mesh(
    base,
    new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }),
  );
  mesh.name = 'mountains';
  return mesh;
}

function treeGeometry(kind: 'round' | 'pine'): THREE.BufferGeometry {
  const b = new GeometryBuilder();
  if (kind === 'round') {
    b.add(T.cylinder, '#8b5e3c', trs([0, 0, 0], [0, 0, 0], [0.22, 1.6, 0.22]));
    b.add(T.ico1, '#6cc04a', trs([0, 2.2, 0], [0, 0, 0], [1.25, 1.1, 1.25]), { color2: '#9be06a' });
    b.add(T.ico1, '#5fb543', trs([0.65, 1.8, 0.2], [0.3, 0, 0], 0.8), { color2: '#8fd862' });
    b.add(T.ico1, '#66bb47', trs([-0.55, 1.9, -0.3], [0, 0.5, 0], 0.75), { color2: '#95dd66' });
  } else {
    b.add(T.cylinder, '#7a5234', trs([0, 0, 0], [0, 0, 0], [0.2, 1.0, 0.2]));
    b.add(T.cone, '#3f9a5c', trs([0, 0.8, 0], [0, 0, 0], [1.2, 1.6, 1.2]), { color2: '#62b874' });
    b.add(T.cone, '#45a262', trs([0, 1.7, 0], [0, 0.4, 0], [0.95, 1.4, 0.95]), {
      color2: '#6cc07c',
    });
    b.add(T.cone, '#4caa68', trs([0, 2.5, 0], [0, 0.8, 0], [0.65, 1.2, 0.65]), {
      color2: '#78c888',
    });
  }
  return b.build().base;
}

function buildTrees(): THREE.Group {
  const group = new THREE.Group();
  const rand = seededRandom(424242);
  const spots: Array<{ x: number; z: number; s: number; kind: 'round' | 'pine' }> = [];
  for (const t of YARD_TREES) spots.push({ x: t.x, z: t.z, s: 1.1 + rand() * 0.3, kind: 'round' });

  // Groves scattered across the plains.
  for (let g = 0; g < 22; g++) {
    const angle = rand() * Math.PI * 2;
    const r = 45 + rand() * 260;
    const cx = Math.cos(angle) * r;
    const cz = Math.sin(angle) * r;
    const kind = rand() > 0.55 ? 'pine' : 'round';
    const count = 3 + Math.floor(rand() * 7);
    for (let i = 0; i < count; i++) {
      const x = cx + (rand() - 0.5) * 22;
      const z = cz + (rand() - 0.5) * 22;
      if (Math.hypot(x - LAYOUT.pasture.x, z - LAYOUT.pasture.z) < LAYOUT.pasture.r + 4) continue;
      if (Math.hypot(x, z) < 36) continue;
      spots.push({ x, z, s: 1 + rand() * 1.1, kind });
    }
  }

  const material = toonUnique({ vertexColors: true });
  for (const kind of ['round', 'pine'] as const) {
    const list = spots.filter((s) => s.kind === kind);
    const mesh = new THREE.InstancedMesh(treeGeometry(kind), material, list.length);
    const m = new THREE.Matrix4();
    list.forEach((spot, i) => {
      m.copy(
        trs([spot.x, groundHeight(spot.x, spot.z) - 0.05, spot.z], [0, rand() * 6.28, 0], spot.s),
      );
      mesh.setMatrixAt(i, m);
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}

function bladeGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.PlaneGeometry(0.13, 1, 1, 3).translate(0, 0.5, 0);
  const pos = geometry.getAttribute('position');
  const colors = new Float32Array(pos.count * 3);
  const normals = geometry.getAttribute('normal');
  const base = new THREE.Color('#78bf58');
  const tip = new THREE.Color('#c4ec8c');
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    pos.setX(i, pos.getX(i) * (1 - y * 0.9));
    c.copy(base).lerp(tip, y);
    colors.set([c.r, c.g, c.b], i * 3);
    // Point normals up so blades are lit like the ground beneath them.
    normals.setXYZ(i, 0, 1, 0);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.deleteAttribute('uv');
  // Duplicate every triangle with reversed winding so blades render from both
  // sides with FrontSide materials (DoubleSide would flip normals and darken them).
  const index = Array.from(geometry.getIndex()?.array ?? []);
  const reversed: number[] = [];
  for (let i = 0; i < index.length; i += 3) reversed.push(index[i]!, index[i + 2]!, index[i + 1]!);
  geometry.setIndex([...index, ...reversed]);
  return geometry;
}

function scatter(
  count: number,
  minR: number,
  maxR: number,
  seed: number,
  accept: (x: number, z: number) => boolean,
) {
  const rand = seededRandom(seed);
  const out: Array<{ x: number; z: number; r: number }> = [];
  let guard = count * 20;
  while (out.length < count && guard-- > 0) {
    const angle = rand() * Math.PI * 2;
    const r = minR + Math.sqrt(rand()) * (maxR - minR);
    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r;
    if (accept(x, z)) out.push({ x, z, r: rand() });
  }
  return out;
}

function buildGrass(count: number): THREE.InstancedMesh {
  const material = addWind(new THREE.MeshLambertMaterial({ vertexColors: true }), 0.22, 'grass');
  const spots = scatter(
    count,
    3,
    62,
    77,
    (x, z) => isOpenGround(x, z, 0.1) && valueNoise(x * 0.25, z * 0.25) > -0.45,
  );
  const mesh = new THREE.InstancedMesh(bladeGeometry(), material, spots.length);
  const m = new THREE.Matrix4();
  const color = new THREE.Color();
  const rand = seededRandom(78);
  spots.forEach((s, i) => {
    const h = 0.25 + rand() * 0.35;
    m.copy(
      trs(
        [s.x, groundHeight(s.x, s.z), s.z],
        [0, rand() * Math.PI, (rand() - 0.5) * 0.3],
        [1 + rand() * 0.6, h, 1],
      ),
    );
    mesh.setMatrixAt(i, m);
    color.setHSL(0.24 + rand() * 0.06, 0.5, 0.88 + rand() * 0.12);
    mesh.setColorAt(i, color);
  });
  mesh.receiveShadow = true;
  mesh.name = 'grass';
  return mesh;
}

const WILDFLOWER_COLORS = ['#ffffff', '#ffd1e8', '#fff3a3', '#d9c2ff', '#ffc9a8', '#bfe3ff'];

function buildWildflowers(count: number): THREE.InstancedMesh {
  const b = new GeometryBuilder();
  b.add(T.cylinder, '#5fae4a', trs([0, 0, 0], [0, 0, 0], [0.015, 0.22, 0.015]));
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    b.add(
      T.sphereLow,
      '#ffffff',
      trs([Math.cos(a) * 0.05, 0.23, Math.sin(a) * 0.05], [0, -a, 0], [0.05, 0.015, 0.03]),
    );
  }
  b.add(T.sphereLow, '#ffe680', trs([0, 0.24, 0], [0, 0, 0], [0.03, 0.02, 0.03]));
  const material = addWind(
    new THREE.MeshLambertMaterial({ vertexColors: true }),
    0.6,
    'wildflower',
  );
  const spots = scatter(count, 10, 170, 99, (x, z) => isOpenGround(x, z, 0.3));
  const mesh = new THREE.InstancedMesh(b.build().base, material, spots.length);
  const m = new THREE.Matrix4();
  const color = new THREE.Color();
  const rand = seededRandom(100);
  spots.forEach((s, i) => {
    m.copy(trs([s.x, groundHeight(s.x, s.z), s.z], [0, rand() * 6.28, 0], 0.8 + rand() * 0.9));
    mesh.setMatrixAt(i, m);
    mesh.setColorAt(i, color.set(WILDFLOWER_COLORS[i % WILDFLOWER_COLORS.length]!));
  });
  mesh.name = 'wildflowers';
  return mesh;
}

function buildPaths(): THREE.InstancedMesh {
  const stones: Array<{ x: number; z: number }> = [];
  const rand = seededRandom(31);
  for (const path of PATHS) {
    for (let i = 0; i < path.length - 1; i++) {
      const [ax, az] = path[i]!;
      const [bx, bz] = path[i + 1]!;
      const length = Math.hypot(bx - ax, bz - az);
      const steps = Math.max(1, Math.round(length / 1.05));
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        stones.push({
          x: ax + (bx - ax) * t + (rand() - 0.5) * 0.3,
          z: az + (bz - az) * t + (rand() - 0.5) * 0.3,
        });
      }
    }
  }
  const geometry = new THREE.CylinderGeometry(0.42, 0.46, 0.1, 9);
  const mesh = new THREE.InstancedMesh(geometry, toon('#e3d9c6'), stones.length);
  const m = new THREE.Matrix4();
  stones.forEach((s, i) => {
    m.copy(
      trs([s.x, 0.03, s.z], [0, rand() * 6.28, 0], [0.8 + rand() * 0.35, 1, 0.8 + rand() * 0.35]),
    );
    mesh.setMatrixAt(i, m);
  });
  mesh.receiveShadow = true;
  return mesh;
}

function buildPond(): THREE.Group {
  const { x, z, r } = LAYOUT.pond;
  const group = new THREE.Group();
  group.position.set(x, 0, z);

  const sand = new THREE.Mesh(new THREE.CircleGeometry(r + 0.7, 40), toon('#e8d9a8'));
  sand.rotation.x = -Math.PI / 2;
  sand.position.y = 0.02;
  sand.receiveShadow = true;
  group.add(sand);

  const water = new THREE.Mesh(
    new THREE.CircleGeometry(r, 40),
    toonUnique({
      color: '#79c8ee',
      emissive: '#2a6f9e',
      emissiveIntensity: 0.25,
      transparent: true,
      opacity: 0.92,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.y = 0.05;
  water.name = 'pond-water';
  group.add(water);

  const shallow = new THREE.Mesh(
    new THREE.RingGeometry(r - 0.6, r, 40),
    toonUnique({ color: '#a5e0f5', transparent: true, opacity: 0.7 }),
  );
  shallow.rotation.x = -Math.PI / 2;
  shallow.position.y = 0.055;
  group.add(shallow);

  const rand = seededRandom(5);
  const stones = new THREE.InstancedMesh(
    T.ico,
    new THREE.MeshLambertMaterial({ color: '#b9b4a8', flatShading: true }),
    22,
  );
  const m = new THREE.Matrix4();
  for (let i = 0; i < 22; i++) {
    const a = (i / 22) * Math.PI * 2 + rand() * 0.2;
    const d = r + 0.35 + rand() * 0.3;
    m.copy(
      trs(
        [Math.cos(a) * d, 0.05, Math.sin(a) * d],
        [rand(), rand(), rand()],
        [0.3 + rand() * 0.25, 0.18 + rand() * 0.1, 0.3 + rand() * 0.2],
      ),
    );
    stones.setMatrixAt(i, m);
  }
  stones.castShadow = true;
  group.add(stones);

  const padGeometry = new THREE.CircleGeometry(0.45, 14, 0.4, Math.PI * 2 - 0.5);
  for (let i = 0; i < 6; i++) {
    const a = rand() * Math.PI * 2;
    const d = rand() * (r - 1);
    const pad = new THREE.Mesh(padGeometry, toon('#5daf55'));
    pad.rotation.set(-Math.PI / 2, 0, rand() * 6.28);
    pad.position.set(Math.cos(a) * d, 0.07, Math.sin(a) * d);
    pad.scale.setScalar(0.7 + rand() * 0.6);
    group.add(pad);
    if (i % 2 === 0) {
      const bloom = new THREE.Mesh(T.sphereLow, toon('#ffb7d5'));
      bloom.scale.set(0.12, 0.08, 0.12);
      bloom.position.set(pad.position.x + 0.1, 0.12, pad.position.z);
      group.add(bloom);
    }
  }

  for (let i = 0; i < 7; i++) {
    const a = 2.2 + i * 0.22 + rand() * 0.1;
    const d = r + 0.2;
    const px = Math.cos(a) * d;
    const pz = Math.sin(a) * d;
    const h = 1.1 + rand() * 0.6;
    const stem = new THREE.Mesh(T.cylinder, toon('#6aa84f'));
    stem.scale.set(0.03, h, 0.03);
    stem.position.set(px, 0, pz);
    stem.rotation.z = (rand() - 0.5) * 0.2;
    const head = new THREE.Mesh(new THREE.CapsuleGeometry(0.06, 0.25, 3, 6), toon('#7a4a2a'));
    head.position.set(px, h - 0.15, pz);
    group.add(stem, head);
  }
  return group;
}

/** The static landscape: ground, mountains, trees, grass, wildflowers, paths and pond. */
export class Terrain {
  readonly group = new THREE.Group();

  constructor(quality: Quality) {
    const high = quality === 'high';
    this.group.add(
      buildGround(),
      buildMountains(),
      buildTrees(),
      buildGrass(high ? 16000 : 6000),
      buildWildflowers(high ? 2600 : 1100),
      buildPaths(),
      buildPond(),
    );
  }
}
